import { useState } from 'react';
import type { Mission, MissionCandidature } from '@/lib/missions';
import {
  CANDIDATURE_STATUT_META,
  MISSION_PLATFORMS,
  computeMissionPayout,
  missionAbonnesTotal,
} from '@/lib/missions';

interface Props {
  mission: Mission;
  candidatures: MissionCandidature[];
  onClose: () => void;
  onFermer: () => Promise<void>;
  onAccepter: (c: MissionCandidature) => Promise<void>;
  onRefuser: (c: MissionCandidature) => Promise<void>;
  onValider: (c: MissionCandidature, vues: number) => Promise<void>;
  onRejeter: (c: MissionCandidature, motif: string) => Promise<void>;
}

function fmt(value: number | null, devise: string): string {
  return `${(Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`;
}

/** Propriétaire : sélectionner les candidats, vérifier les publications et payer. */
export default function MissionDetailModal({
  mission,
  candidatures,
  onClose,
  onFermer,
  onAccepter,
  onRefuser,
  onValider,
  onRejeter,
}: Props) {
  const devise = mission.monaie || 'MAD';
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [vues, setVues] = useState<Record<number, string>>({});
  const [motif, setMotif] = useState<Record<number, string>>({});
  const [openMotif, setOpenMotif] = useState<number | null>(null);
  const [previewLink, setPreviewLink] = useState<{ label: string; url: string } | null>(null);

  const run = async (id: number, action: () => Promise<void>) => {
    setBusyId(id);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action impossible.');
    } finally {
      setBusyId(null);
    }
  };

  const totalDu = candidatures
    .filter((c) => c.statut === 'validee')
    .reduce((sum, c) => sum + (Number(c.montant_du) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
          <div className="min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{mission.titre}</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {fmt(mission.tarif_1000_vues, devise)} / 1000 vues
              {mission.ville ? ` · ${mission.ville}` : ''}
              {' · '}{candidatures.length} candidature{candidatures.length > 1 ? 's' : ''}
              {totalDu > 0 ? ` · ${fmt(totalDu, devise)} validés` : ''}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {mission.description && (
            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{mission.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            {(mission.plateformes || []).map((key) => {
              const meta = MISSION_PLATFORMS.find((p) => p.key === key);
              return (
                <span key={key} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
                  <i className={`${meta?.icon || 'ri-share-line'} mr-1`}></i>{meta?.label || key}
                </span>
              );
            })}
            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-medium whitespace-nowrap">
              <i className="ri-music-2-line mr-1"></i>
              {mission.musique_autorisee ? 'Musique autorisée' : 'Musique non autorisée'}
            </span>
          </div>

          {(mission.media || []).filter((m) => m && m.url).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(mission.media || []).filter((m) => m && m.url).map((m, i) => (
                <div key={`${m.url}-${i}`} className="w-24 h-24 rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
                  {m.type === 'video' ? (
                    <video src={m.url} className="w-full h-full object-cover" muted playsInline controls />
                  ) : (
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              ))}
            </div>
          )}

          {mission.statut === 'ouverte' && (
            <button
              onClick={() => run(0, onFermer)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-secondary-600 transition-colors"
            >
              <i className="ri-lock-line"></i>
              Fermer les candidatures et sélectionner
            </button>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}

          {candidatures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-user-search-line text-xl text-foreground-400"></i>
              </div>
              <p className="text-sm font-medium text-foreground-700">Aucune candidature</p>
              <p className="text-xs text-foreground-500 mt-1 max-w-sm">
                Diffusez votre mission depuis la liste pour toucher les commerces et les créateurs.
              </p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {candidatures.map((c) => {
                const meta = CANDIDATURE_STATUT_META[c.statut] || CANDIDATURE_STATUT_META.en_attente;
                const liens = Object.entries(c.liens || {}).filter(([, v]) => !!v);
                const abonnes = c.abonnes || {};
                const vuesText = vues[c.id] ?? (c.vues_declarees ? String(c.vues_declarees) : '');
                const montantEstime = computeMissionPayout(mission.tarif_1000_vues, Number(vuesText) || 0);
                return (
                  <li key={c.id} className="rounded-lg border border-background-200/70 bg-background-50 p-3.5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-foreground-900 truncate">{c.auteur_nom || 'Candidat'}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                          <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
                            <i className="ri-group-line mr-1"></i>
                            {missionAbonnesTotal(abonnes).toLocaleString('fr-FR')} abonnés
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-1.5">
                          {liens.map(([key, value]) => {
                            const p = MISSION_PLATFORMS.find((x) => x.key === key);
                            return (
                              <span key={key} className="inline-flex items-center gap-1.5">
                                <a
                                  href={value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[11px] text-primary-600 hover:text-primary-700 no-underline inline-flex items-center gap-1"
                                >
                                  <i className={p?.icon || 'ri-link'}></i>
                                  {p?.label || key}
                                  {abonnes[key] ? ` · ${Number(abonnes[key]).toLocaleString('fr-FR')}` : ''}
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setPreviewLink({ label: `${p?.label || key} — ${c.auteur_nom || 'Candidat'}`, url: value })}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 border border-background-200/70 text-[10px] font-semibold text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70 whitespace-nowrap cursor-pointer transition-colors"
                                  title="Analyser ce profil sans quitter Zifek"
                                >
                                  <i className="ri-eye-line"></i>
                                  Visualiser
                                </button>
                              </span>
                            );
                          })}
                        </div>

                        {c.message && <p className="text-xs text-foreground-600 mt-1.5 whitespace-pre-line">{c.message}</p>}

                        {c.lien_publication && (
                          <a
                            href={c.lien_publication}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-[11px] font-semibold no-underline whitespace-nowrap hover:bg-primary-100 transition-colors"
                          >
                            <i className="ri-external-link-line"></i>
                            Voir la publication
                          </a>
                        )}

                        {c.statut === 'validee' && (
                          <p className="mt-1.5 text-[11px] text-accent-800">
                            {(Number(c.vues_validees) || 0).toLocaleString('fr-FR')} vues validées · {fmt(c.montant_du, devise)}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {c.statut === 'en_attente' && (
                          <>
                            <button
                              onClick={() => run(c.id, () => onAccepter(c))}
                              disabled={busyId === c.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                            >
                              {busyId === c.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                              Accepter
                            </button>
                            <button
                              onClick={() => run(c.id, () => onRefuser(c))}
                              disabled={busyId === c.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
                            >
                              <i className="ri-close-line"></i>
                              Refuser
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {(c.statut === 'livree' || c.statut === 'rejetee') && (
                      <div className="mt-3 pt-3 border-t border-background-200/60">
                        <p className="text-[11px] text-foreground-500 mb-2">
                          Vues déclarées par le candidat : <strong className="text-foreground-800">{(Number(c.vues_declarees) || 0).toLocaleString('fr-FR')}</strong>
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                          <div className="flex-1 min-w-0">
                            <label className="block text-[11px] font-medium text-foreground-600 mb-1">Vues retenues</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={vuesText}
                              onChange={(e) => setVues((prev) => ({ ...prev, [c.id]: e.target.value.replace(/[^0-9]/g, '') }))}
                              placeholder="ex. 12500"
                              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                            />
                            <p className="mt-1 text-[11px] text-foreground-500">Montant estimé : <strong className="text-foreground-900">{fmt(montantEstime, devise)}</strong></p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => run(c.id, () => onValider(c, Number(vuesText) || 0))}
                              disabled={busyId === c.id || !vuesText}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors"
                            >
                              {busyId === c.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-checkbox-circle-line"></i>}
                              Valider et payer
                            </button>
                            <button
                              onClick={() => setOpenMotif(openMotif === c.id ? null : c.id)}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-red-50 text-red-600 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-red-100 transition-colors"
                            >
                              <i className="ri-close-circle-line"></i>
                              Rejeter
                            </button>
                          </div>
                        </div>
                        {openMotif === c.id && (
                          <div className="mt-2 flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              value={motif[c.id] || ''}
                              onChange={(e) => setMotif((prev) => ({ ...prev, [c.id]: e.target.value }))}
                              placeholder="Motif du rejet (vues insuffisantes…)"
                              className="flex-1 min-w-0 px-3 py-2 bg-background-100 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                            />
                            <button
                              onClick={() => run(c.id, async () => { await onRejeter(c, motif[c.id] || ''); setOpenMotif(null); })}
                              disabled={busyId === c.id}
                              className="px-3 py-2 rounded-full bg-red-500 text-white text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-red-600 disabled:opacity-50 transition-colors"
                            >
                              Confirmer le rejet
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Aperçu intégré du lien candidat (iframe) : tout analyser sans quitter Zifek */}
      {previewLink && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6">
          <div className="absolute inset-0 bg-black/60" onClick={() => setPreviewLink(null)} />
          <div className="relative w-full max-w-5xl h-[85vh] bg-background-50 rounded-xl border border-background-200/70 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 h-14 border-b border-background-200/70 flex-shrink-0">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900 truncate">{previewLink.label}</p>
                <p className="text-[11px] text-foreground-400 truncate">{previewLink.url}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={previewLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-[11px] font-semibold text-foreground-600 hover:bg-background-200/70 no-underline whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  Nouvel onglet
                </a>
                <button
                  onClick={() => setPreviewLink(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 bg-background-100">
              <iframe
                src={previewLink.url}
                title={previewLink.label}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="px-4 py-2 border-t border-background-200/70 flex-shrink-0">
              <p className="text-[11px] text-foreground-400">
                <i className="ri-information-line mr-1"></i>
                Si la page reste vide, le réseau social bloque l&apos;affichage intégré : utilisez « Nouvel onglet ».
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}